<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="map-container" :class="{ 'active-image': isActiveImage }" @click="isActiveImage = true" ref="container">
  <template v-if="!loading && zoom !== null">
    <div class="image-title">
      <!-- {{ image.labelText || image.instanceFilename }} -->
      <image-name :image="image" />
    </div>
    <vl-map
      :data-projection="projectionName"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      :keyboard-event-target="document"
      @pointermove="projectedMousePosition = $event.coordinate"
      @mounted="updateKeyboardInteractions"
      ref="map"
    >

      <vl-view
        :center.sync="center"
        :zoom.sync="zoom"
        :rotation.sync="rotation"
        :max-zoom="maxZoom"
        :max-resolution="Math.pow(2, image.zoom)"
        :extent="extent"
        :projection="projectionName"
        @mounted="viewMounted()"
        ref="view"
        :zoom-factor="2 ** (1 / ZOOM_STEP_PER_LEVEL)"
      />

      <vl-layer-tile :extent="extent" @mounted="addOverviewMap" ref="baseLayer">
        <vl-source-cytomine
          :projection="projectionName"
          :url="baseLayerURL"
          :tile-load-function="tileLoadFunction"
          :size="imageSize"
          :extent="extent"
          :nb-resolutions="image.zoom"
          ref="baseSource"
          @mounted="setBaseSource()"
          :transition="0"
          :tile-size="[tileSize, tileSize]"
        />
      </vl-layer-tile>

<!--      <vl-layer-image>-->
<!--        <vl-source-raster-->
<!--          v-if="baseSource && colorManipulationOn"-->
<!--          :sources="[baseSource]"-->
<!--          :operation="operation"-->
<!--          :lib="lib"-->
<!--        />-->
<!--      </vl-layer-image>-->

      <annotation-layer
        v-for="layer in selectedLayers"
        :key="'layer-'+layer.id"
        :index="index"
        :layer="layer"
      />

      <select-interaction v-if="activeSelectInteraction" :index="index" />
      <draw-interaction v-if="activeDrawInteraction" :index="index" />
      <modify-interaction v-if="activeModifyInteraction" :index="index" />

    </vl-map>

    <!-- DrawTools moved to CytomineViewer.vue -->

    <!-- Panels moved to CytomineViewer.vue -->

    <image-controls :index="index" class="image-controls-wrapper" />

    <div class="broadcast" v-if="imageWrapper.tracking.broadcast">
      <i class="fas fa-circle"></i> {{$t('live')}}
    </div>

    <scale-line v-show="scaleLineCollapsed" :image="image" :zoom="zoom" :mousePosition="projectedMousePosition" />

    <toggle-scale-line :index="index" />

    <annotations-container :index="index" @centerView="centerViewOnAnnot" />

    <div class="custom-overview" ref="overview">
      <!-- <p class="image-name" :class="{hidden: overviewCollapsed}">
        <image-name :image="image" />
      </p> -->
    </div>

    <div class="custom-label" ref="label" v-if="image.macroURL && !isBlindMode">
      <div class="label-toggle-wrapper">
        <div v-if="labelCollapsed">
          <button class="button is-small" @click.stop="labelCollapsed = false">
            <span class="fas fa-angle-double-left"></span>
          </button>
        </div>
        <div v-else>
          <button class="button is-small" @click.stop="labelCollapsed = true">
            <span class="fas fa-angle-double-right"></span>
          </button>
        </div>
      </div>
      <div class="image-label" :class="{hidden: labelCollapsed}" @click="isMetadataModalActive = true">
        <image-thumbnail :image="image" :macro="true" :onlyLabel="true" :blindMode="blindMode" :size="512" :key="`${image.id}-macro-256`" :extra-parameters="{Authorization: 'Bearer ' + shortTermToken }"/>
      </div>
    </div>

    <snapshot-modal
      :active.sync="openSnapshotModal" 
      :imageId = "image.id"
      :imageName = "image.labelText || image.instanceFilename"
      :totalAnnotationIds="totalAnnotationIds"
      :selectedAnnotationIds="selectedAnnotationIds"
      :snapshotArea = "currentArea"
    />
    
    <!-- attached file - image window -->
    <attached-image-dialog ref="attachedImageDialog" :initial-viewers.sync="attachedImages" class="overlay-dialog" />
  </template>
  
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import _ from 'lodash';

import ImageName from '@/components/image/ImageName';
import ImageThumbnail from '@/components/image/ImageThumbnail' ;
import AnnotationLayer from './AnnotationLayer';
import ScaleLine from './ScaleLine';
import ImageControls from './ImageControls';
import AnnotationsContainer from './AnnotationsContainer';

// Panel components moved to CytomineViewer.vue

import SelectInteraction from './interactions/SelectInteraction';
import DrawInteraction from './interactions/DrawInteraction';
import ModifyInteraction from './interactions/ModifyInteraction';
import ToggleScaleLine from './interactions/ToggleScaleLine';

import SnapshotModal from './panels/SnapshotModal';

import {addProj, createProj, getProj} from 'vuelayers/lib/ol-ext';

import View from 'ol/View';
import OverviewMap from 'ol/control/OverviewMap';
import {KeyboardPan, KeyboardZoom} from 'ol/interaction';
import {noModifierKeys, targetNotEditable} from 'ol/events/condition';
import WKT from 'ol/format/WKT';

import {Cytomine, ImageConsultation, Annotation, AnnotationType, UserPosition, SliceInstance, AnnotationCollection} from 'cytomine-client';

// import {constLib, operation} from '@/utils/color-manipulation.js';

import constants from '@/utils/constants.js';
import AttachedImageDialog from './AttachedImageDialog.vue';

export default {
  name: 'cytomine-image',
  props: {
    index: String
  },
  components: {
    ImageName,
    ImageThumbnail,

    AnnotationLayer,

    ScaleLine,
    ImageControls,
    AnnotationsContainer,

    // Panel components moved to CytomineViewer.vue

    SelectInteraction,
    DrawInteraction,
    ModifyInteraction,
    ToggleScaleLine,

    SnapshotModal,
    AttachedImageDialog,
  },
  data() {
    return {
      minZoom: 0,

      projectedMousePosition: [0, 0],

      baseSource: null,
      routedAnnotation: null,
      selectedAnnotation: null,

      timeoutSavePosition: null,

      loading: true,

      overview: null,

      format: new WKT(),

      ZOOM_STEP_PER_LEVEL: constants.ZOOM_STEP_PER_LEVEL,

      openSnapshotModal: false,
      
      totalAnnotationIds: '',
      selectedAnnotationIds: '',
      currentArea: {},
      attachedImages: [],
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    blindMode() {
      return ((this.$store.state.currentProject.project || {}).blindMode) || false;
    },
    document() {
      return document;
    },
    project: get('currentProject/project'),
    routedAction() {
      return this.$route.query.action;
    },
    configUI: get('currentProject/configUI'),
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    /**
     * BLIND   MANAGER    RESULT
     * 0       0          1
     * 0       1          1
     * 1       0          0
     * 1       1          1
     */
    isBlindMode() {
      return this.blindMode;
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    nbImages() {
      return Object.keys(this.viewerWrapper.images).length;
    },
    imageWrapper() {
      return this.viewerWrapper.images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    canEdit() {
      return this.$store.getters['currentProject/canEditImage'](this.image);
    },
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    projectionName() {
      return `CYTO-${this.image.id}`;
    },
    terms() {
      return this.$store.getters['currentProject/terms'];
    },
    selectedLayers() {
      return this.imageWrapper.layers.selectedLayers || [];
    },
    isActiveImage: {
      get() {
        return this.viewerWrapper.activeImage === this.index;
      },
      set(value) {
        if(value) {
          if(this.viewerWrapper) {
            this.$store.commit(this.viewerModule + 'setActiveImage', this.index);
          }
        }
        else {
          throw new Error('Cannot unset active map');
        }
      }
    },
    activeTool() {
      return this.imageWrapper.draw.activeTool;
    },
    activeEditTool() {
      return this.imageWrapper.draw.activeEditTool;
    },
    maxZoom() {
      return this.$store.getters[this.imageModule + 'maxZoom'];
    },

    center: {
      get() {
        return this.imageWrapper.view.center;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setCenter', {index: this.index, center: value});
      }
    },
    zoom: {
      get() {
        return this.imageWrapper.view.zoom;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setZoom', {index: this.index, zoom: Number(value)});
      }
    },
    rotation: {
      get() {
        return this.imageWrapper.view.rotation;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setRotation', {index: this.index, rotation: Number(value)});
      }
    },

    viewState() {
      return {center: this.center, zoom: this.zoom, rotation: this.rotation};
    },

    extent() {
      return [0, 0, this.image.width, this.image.height];
    },
    imageSize() {
      return [this.image.width, this.image.height];
    },
    tileSize() {
      return this.image.tileSize;
    },
    baseLayerProcessingParams() {
      return this.$store.getters[this.imageModule + 'tileRequestParams'];
    },
    baseLayerSliceParams() {
      return {
        zSlices: this.slices[0].zStack,
        timepoints: this.slices[0].time
      };
    },
    baseLayerURLQuery() {
      let query = new URLSearchParams({...this.baseLayerSliceParams, ...this.baseLayerProcessingParams}).toString();
      if (query.length > 0) {
        return `?${query}`;
      }
      return query;
    },
    baseLayerURL() {
      let slice = this.slices[0];
      return Cytomine.instance.host + Cytomine.instance.basePath + `sliceinstance/${slice.id}/normalized-tile/zoom/{z}/tx/{x}/ty/{y}.jpg${this.baseLayerURLQuery}`;
    },
    tileLoadFunction() {
      return (tile, src) => {
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.open('GET', src);
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.shortTermToken);
        xhr.addEventListener('load', () => {
          const url = URL.createObjectURL(xhr.response);
          const tileImage = tile.getImage();
          tileImage.addEventListener('load', () => URL.revokeObjectURL(url));
          tileImage.src = url;
        });
        xhr.send();
      };
    },

    layersToPreload() {
      let layers = [];
      let annot = this.selectedAnnotation || this.routedAnnotation;
      if (annot) {
        layers.push(annot.type === AnnotationType.REVIEWED ? -1 : (annot.user ?? annot.annotationLayer));
      }
      if (this.routedAction === 'review' && !layers.includes(-1)) {
        layers.push(-1);
      }
      return layers;
    },

    overviewCollapsed() {
      return this.overview ? this.overview.getCollapsed() : this.imageWrapper.view.overviewCollapsed;
    },
    scaleLineCollapsed() {
      return !this.imageWrapper.view.scaleLineCollapsed;
    },
    labelCollapsed: {
      get() {
        return this.imageWrapper.view.labelCollapsed;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setLabelCollapsed', value);
      }
    },

    correction() {
      return ['correct-add', 'correct-remove'].includes(this.activeEditTool);
    },
    activeSelectInteraction() {
      return this.activeTool === 'select';
    },
    activeDrawInteraction() {
      if (this.image.reviewed) {
        return false;
      }

      return !this.activeSelectInteraction || this.correction;
    },
    activeModifyInteraction() {
      return this.activeSelectInteraction && this.activeEditTool && !this.correction;
    },
    idealZoom() {
      let container = this.$refs.container;
      let idealZoom = this.maxZoom;
      let factor = this.maxZoom - this.image.zoom;
      let mapWidth = this.image.width * Math.pow(2, factor);
      let mapHeight = this.image.height * Math.pow(2, factor);
      while(mapWidth > container.clientWidth || mapHeight > container.clientHeight) {
        mapWidth /= 2;
        mapHeight /= 2;
        idealZoom --;
      }
      return idealZoom * this.ZOOM_STEP_PER_LEVEL;
    }
  },
  watch: {
    viewState() {
      this.savePosition();
    },
    overviewCollapsed(value) {
      this.$store.commit(this.imageModule + 'setOverviewCollapsed', value);
    }
  },
  methods: {
    setInitialZoom() {
      if(this.zoom !== null) {
        return; // not the first time the viewer is opened => zoom was already initialized
      }
      this.zoom = this.idealZoom;
    },

    async updateMapSize() {
      await this.$nextTick();
      if(this.$refs.map) {
        // Store current view state before update
        let currentCenter = null;
        let currentZoom = null;
        
        if(this.$refs.view && this.$refs.view.$view) {
          const view = this.$refs.view.$view;
          currentCenter = view.getCenter();
          currentZoom = view.getZoom();
        }
        
        // Update map size
        this.$refs.map.updateSize();
        
        // Restore view state to maintain viewport
        if(currentCenter && currentZoom && this.$refs.view && this.$refs.view.$view) {
          const view = this.$refs.view.$view;
          view.setCenter(currentCenter);
          view.setZoom(currentZoom);
        }
      }
    },

    async updateKeyboardInteractions() {
      await this.$refs.map.$createPromise; // wait for ol.Map to be created

      this.$refs.map.$map.getInteractions().forEach(interaction => {
        if(interaction instanceof KeyboardPan || interaction instanceof KeyboardZoom) {
          interaction.condition_ = (mapBrowserEvent) => {
            return noModifierKeys(mapBrowserEvent)
              && targetNotEditable(mapBrowserEvent)
              && this.isActiveImage
              && !mapBrowserEvent.originalEvent.target.classList.contains('ql-editor');
          };
        }
      });
    },

    async viewMounted() {
      await this.$refs.view.$createPromise; // wait for ol.View to be created

      // 전역 Store에 저장된 이 이미지의 마지막 회전값이 있는지 확인
      const savedRotation = this.$store.state.currentProject.imageRotations[this.image.id];

      // 백업된 값이 있으면 복구, 없으면 0으로 강제 초기화
      if (savedRotation !== undefined) {
        this.rotation = savedRotation;
      }
      else {
      // 이전에 이 슬롯(index)에 있던 다른 이미지의 각도가 남아있을 수 있으므로 0으로 리셋
        this.rotation = 0; 
      }

      if(this.routedAnnotation) {
        this.centerViewOnAnnot(this.routedAnnotation, 500);
      }
      this.savePosition();
    },

    async setBaseSource() {
      await this.$refs.baseSource.$createPromise;
      this.baseSource = this.$refs.baseSource.$source;
    },

    async addOverviewMap() {
      if(!this.isPanelDisplayed('overview')) {
        return;
      }

      await this.$refs.map.$createPromise; // wait for ol.Map to be created
      await this.$refs.baseLayer.$createPromise; // wait for ol.Layer to be created

      let map = this.$refs.map.$map;

      this.overview = new OverviewMap({
        view: new View({projection: this.projectionName}),
        layers: [this.$refs.baseLayer.$layer],
        tipLabel: this.$t('overview'),
        target: this.$refs.overview,
        collapsed: this.imageWrapper.view.overviewCollapsed
      });
      map.addControl(this.overview);

      this.overview.getOverviewMap().on(('click'), (evt) => {
        let size = map.getSize();
        map.getView().centerOn(evt.coordinate, size, [size[0]/2, size[1]/2]);
      });
    },

    toggleOverview() {
      if (this.overview) {
        this.overview.setCollapsed(!this.imageWrapper.view.overviewCollapsed);
      }
    },

    toggleLabel() {
      this.$store.commit(this.imageModule + 'setLabelCollapsed', !this.imageWrapper.view.labelCollapsed);
    },

    // togglePanel method moved to CytomineViewer.vue

    savePosition: _.debounce(async function() {
      if(this.$refs.view) {
        let extent = this.$refs.view.$view.calculateExtent(); // [minX, minY, maxX, maxY]
        try {
          await UserPosition.create({
            image: this.image.id,
            slice: this.slices[0].id,
            zoom: this.zoom,
            rotation: this.rotation,
            bottomLeftX: Math.round(extent[0]),
            bottomLeftY: Math.round(extent[1]),
            bottomRightX: Math.round(extent[2]),
            bottomRightY: Math.round(extent[1]),
            topLeftX: Math.round(extent[0]),
            topLeftY: Math.round(extent[3]),
            topRightX: Math.round(extent[2]),
            topRightY: Math.round(extent[3]),
            broadcast: this.imageWrapper.tracking.broadcast
          });
        }
        catch(error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-save-user-position')});

          // 404일 때 worklist 화면으로 이동
          if (error.response && error.response.status === 404) {
            this.$eventBus.$emit('refreshWorklist');
            const savedHierarchy = JSON.parse(localStorage.getItem('worklistHierarchy') || '{}');
            this.$router.push(`/worklist/${savedHierarchy.sortPropWorklist}-${savedHierarchy.idWorklist}`);
          }
        }

        clearTimeout(this.timeoutSavePosition);
        this.timeoutSavePosition = setTimeout(this.savePosition, constants.SAVE_POSITION_IN_IMAGE_INTERVAL);
      }
    }, 500),

    fitZoom() {
      this.$refs.view.animate({
        zoom: this.idealZoom,
        center: [this.image.width/2, this.image.height/2]
      });
    },

    async centerViewOnAnnot(annot, duration) {
      if (annot.image === this.image.id) {
        if (!annot.location) {
          //in case annotation location has not been loaded
          annot = (await Cytomine.instance.api.get(`/annotations/${annot.id}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        }

        if (annot.project !== this.project.id) {
          await this.$router.push(`/case/${annot.project}/image/${annot.image}/annotation/${annot.id}`);
        }

        let geometry = this.format.readGeometry(annot.location);
        await this.$refs.view.fit(geometry, {duration, padding: [10, 10, 10, 10], maxZoom: this.image.zoom * this.ZOOM_STEP_PER_LEVEL});

        if (!Object.prototype.hasOwnProperty.call(annot, 'centroid')) {
          return;
        }

        // HACK: center set by view.fit() is incorrect => reset it manually
        this.center = (geometry.getType() === 'Point') ? geometry.getFirstCoordinate()
          : [annot.centroid.x, annot.centroid.y];
        // ---
      }
    },

    async selectAnnotationHandler({index, annot, center=false, showComments=false}) {
      if (this.index === index && annot.image === this.image.id) {
        try {
          let sliceChange = false;
          if (!annot.slice) {
            //in case annotation slice has not been loaded
            annot = await Annotation.fetch(annot.id);
          }

          if(!this.sliceIds.includes(annot.slice)) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
            this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id, hard: true});
            sliceChange = true;
          }

          if (showComments) {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }

          this.selectedAnnotation = annot; // used to pre-load annot layer
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
          this.$eventBus.$emit('selectAnnotationInLayer', {index, annot});

          if (center) {
            await this.viewMounted();
            let duration = (sliceChange) ? undefined : 500;
            this.centerViewOnAnnot(annot, duration);
          }
        }
        catch(error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
        }
      }
    },

    isPanelDisplayed(panel) {
      return this.configUI[`project-explore-${panel}`];
    },
    shortkeyHandler(key) {
      if(!key.startsWith('toggle-all-') && !this.isActiveImage) { // shortkey should only be applied to active map
        return;
      }

      // Panel toggle shortcuts now handled by CytomineViewer.vue
      key = key.replace('toggle-all-', 'toggle-');
      switch(key) {
        case 'toggle-overview':
          if (this.isPanelDisplayed('overview')) {
            this.toggleOverview();
          }
          return;
        case 'toggle-label':
          if (this.isPanelDisplayed('label')) {
            this.toggleLabel();
          }
          return;
      }
    },
    async takeScreenshot() {
      // TODO: 특정 tag를 가진 annotation 만 추가하고 싶은 경우에 대한 처리 필요
      // 특정 tag에 대해서만 annotation 을 표시하는 기능 선행 처리

      // 현재 이미지의 모든 annot ids
      let annotationCollection = new AnnotationCollection({ image: this.image.id });
      let annotations = await annotationCollection.fetchAll();
      this.totalAnnotationIds = annotations._data.map(annot => annot.id).join(',');

      // 현재 선택된 annot ids
      const features = this.viewerWrapper?.images?.[this.index]?.selectedFeatures?.selectedFeatures;
      const annotationsIds = Array.isArray(features) && features.length > 0 ? features.map(f => f.id).join(',') : '';
      this.selectedAnnotationIds = annotationsIds;

      // 현재 화면의 원본 이미지 기준 extent 계산
      const view = this.$refs.view;
      if (!view || !view.$view) return;

      const extent = view.$view.calculateExtent();
      let left = Math.floor(extent[0]);
      let bottom = Math.floor(extent[1]);
      let right = Math.floor(extent[2]);
      let top = Math.floor(extent[3]);

      // Cytomine 좌표계는 y=0이 위쪽. OL은 아래쪽.
      // 따라서 이미지 높이(imageHeight)를 알아야 함.
      const imageWidth = this.image.width;
      const imageHeight = this.image.height;
      
      left = Math.max(0, left);
      bottom = Math.max(0, bottom);
      right = Math.min(imageWidth, right);
      top = Math.min(imageHeight, top);
      
      const w = right - left;
      const h = top - bottom;
      const x = left;
      const y = imageHeight - top;

      this.currentArea = {left, right, top, bottom, x, y, w, h};

      this.openSnapshotModal = true;
    },

    // Event handlers for viewer panel interactions
    handleResetZoom(event) {
      if (event.index === this.index) {
        const targetZoom = this.image.zoom * this.ZOOM_STEP_PER_LEVEL;
        
        this.$refs.view.animate({
          zoom: targetZoom,
        }, () => {
          this.$store.dispatch(this.viewerModule + 'setZoom', {
            index: this.index, 
            zoom: targetZoom
          });
        });
      }
    },
    handleFitZoom(event) {
      if (event.index === this.index) {
        this.fitZoom();
      }
    },

    handleAnimateView(event) {
      if (event.index === this.index && this.$refs.view) {
        this.$refs.view.animate(event.options);
      }
    },

    handleSetZoomLevel(event) {
      if (event.index === this.index && this.$refs.view) {
        const zoomLevel = (Math.log2(event.level / this.image.magnification) + this.image.zoom) * this.ZOOM_STEP_PER_LEVEL;
        const targetZoom = Math.min(zoomLevel, this.maxZoom);

        if (this.$refs.view.$view) {
          this.$refs.view.$view.setMaxZoom(this.maxZoom);
        }

        console.log(`Setting zoom level to ${event.level} (targetZoom: ${targetZoom}, image.zoom: ${this.image.zoom}, magnification: ${this.image.magnification})`);

        this.$refs.view.animate({ zoom: targetZoom }, () => {
          this.zoom = targetZoom; // Ensure zoom is updated in the store after animation
        });
      }
    },

    handleTakeScreenshot(event) {
      if (event.index === this.index) {
        this.takeScreenshot();
      }
    },
    async handleOpenViewer(file) {
      // 이미 attachedImages에 있는지 확인
      const existing = this.attachedImages.find(img => img.id === file.id);

      if (existing) {
        // 이미 존재하면, viewers 배열에서 해당 viewer 찾아 zIndex 최상위로
        this.$refs.attachedImageDialog.bringToFrontById(existing.id);
      }
      else {
        if (this.attachedImages.some(v => v.id === file.id)) return;

        // 없으면 새로 추가
        this.attachedImages.push({
          fileName: file.fileName,
          fileUrl: file.fileUrl,
          id: file.id,
        });
      }
    },
    handleCloseViewer(viewer) {
      console.log('>> on handler: handleCloseViewer', this.attachedImages, viewer);
      this.$refs.attachedImageDialog.closeByFildId(viewer.id);
      this.attachedImages = this.attachedImages.filter(
        img => img.id !== viewer.id
      );
    }
  },
  async created() {
    if(!getProj(this.projectionName)) { // if image opened for the first time
      let projection = createProj({code: this.projectionName, units: 'pixels', extent: this.extent});
      addProj(projection);
    }

    if(this.routedAction === 'review') {
      // this.togglePanel('review');
      if(!this.image.inReview) {
        try {
          let clone = await this.image.clone().review();
          this.$store.commit(this.imageModule + 'setImageInstance', clone);
        }
        catch(error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-start-review')});
        }
      }
      this.$store.commit(this.imageModule + 'setReviewMode', true);
    }

    // remove all selected features in order to reselect them when they will be added to the map (otherwise,
    // issue with the select interaction)
    this.selectedLayers.forEach(layer => {
      this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', {layer, cache: true});
    });

    let annot = this.imageWrapper.routedAnnotation;
    if (!annot) {
      let idRoutedAnnot = this.$route.params.idAnnotation;
      if (idRoutedAnnot) {
        try {
          annot = (await Cytomine.instance.api.get(`/annotations/${idRoutedAnnot}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        }
        catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
        }
      }
    }

    if (annot) {
      if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
        let response = await Cytomine.instance.api.get(`/annotation-layers/${annot.annotationLayer}/task-run-layer`);
        let taskRunLayer = response.data;
        annot.image = taskRunLayer.image;
      }

      try {
        if (annot.image === this.image.id) {
          if (!this.sliceIds.includes(annot.slice) && Object.prototype.hasOwnProperty.call(annot, 'slice')) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
          }
          this.routedAnnotation = annot;
          if (this.routedAction === 'comments') {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        }

        this.$store.commit(this.imageModule + 'clearRoutedAnnotation');
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
      }
    }

    try {
      await new ImageConsultation({image: this.image.id}).save();
    }
    catch(error) {
      console.log(error);
      this.$notify({type: 'error', text: this.$t('notif-error-save-image-consultation')});
    }


  
    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('updateMapSize', this.updateMapSize);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$on('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$on('resetZoom', this.handleResetZoom);
    this.$eventBus.$on('fitZoom', this.handleFitZoom);
    this.$eventBus.$on('animateView', this.handleAnimateView);
    this.$eventBus.$on('setZoomLevel', this.handleSetZoomLevel);
    this.$eventBus.$on('takeScreenshot', this.handleTakeScreenshot);
    this.setInitialZoom();
    this.$eventBus.$on('openImageWindow', this.handleOpenViewer);
    this.$eventBus.$on('closeImageWindow', this.handleCloseViewer);
    this.$nextTick(() => {
      const lastIndex = Object.keys(this.viewerWrapper.images).slice(-1)[0];

      if (this.index === lastIndex) {
        this.$store.commit(this.viewerModule + 'setActiveImage', this.index);
      }
    });
  },
  beforeDestroy() {
    this.$eventBus.$off('updateMapSize', this.updateMapSize);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$off('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$off('resetZoom', this.handleResetZoom);
    this.$eventBus.$off('fitZoom', this.handleFitZoom);
    this.$eventBus.$off('animateView', this.handleAnimateView);
    this.$eventBus.$off('setZoomLevel', this.handleSetZoomLevel);
    this.$eventBus.$off('takeScreenshot', this.handleTakeScreenshot);
    clearTimeout(this.timeoutSavePosition);
    this.$eventBus.$off('openImageWindow', this.handleOpenViewer);
    this.$eventBus.$off('closeImageWindow', this.handleCloseViewer);
  }
};
</script>

<style lang="scss">
@import '~vuelayers/lib/style.css';

// Panel variables moved to CytomineViewer.vue

.map-container {
  border: 2px solid transparent;
}

.map-container.active-image {
  border: 2px solid #3273dc; /* 원하는 색 */
}

.map-container {
  display:flex;
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #e5e7eb; /* 뷰어 배경보다 살짝 더 짙은 Gray */
}

.map {
  flex-grow: 1;
}

/* DrawTools moved to CytomineViewer.vue */

.broadcast {
  position: absolute;
  right: 4.5rem;
  top: 0.7em;
  text-transform: uppercase;
  font-weight: 600;
  background-color: #EE4242;
  color: white;
  padding: 0.25em 0.75em 0.25em 0.55em;
  border-radius: 5px;
  border: 2px solid white;

  i.fas {
    margin-right: 0.3em;
  }
}

/* Panel styles moved to CytomineViewer.vue */

/* ----- CUSTOM STYLE FOR OL CONTROLS ----- */

.ol-zoom, .ol-rotate {
  display: none !important; /* TODO: Hide zoom controls properly */
  background: none !important;
  z-index: 20;
}

.ol-rotate:not(.custom) {
  display: none;
}

.ol-control button {
  background: white !important;
  color: black !important;
  border-radius: 2px !important;
  box-shadow: 0 0 1px #777;

  &:hover {
    box-shadow: 0 0 1px black;
    cursor: pointer;
  }
}

.ol-zoom-in {
  margin-bottom: 5px !important;
}

.custom-overview {
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  border-radius: 4px;

  .ol-overviewmap {
    position: static;
    background: none;
  }

  .ol-overviewmap button {
    top: 0.2em !important;
    right: 0.2em !important;
    bottom: auto !important;
    left: auto !important;
  }

  .image-name {
    font-size: 0.8em;
    padding: 2px 5px;
    width: 158px;
    word-wrap: break-word;

    &.hidden {
      display: none;
    }
  }
}

.custom-label {
  position: absolute;
  top: 0.5em;
  right: 164px;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;

  .label-toggle-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    background: white;
    border-radius: 2px;
    font-size: 5px;
    padding: 0.5em;
    
    .button {
      height: 1.5em;
      width: 1.5em;
      padding: 3px;
    }
  }

  .image-label {
    max-width: 11rem;
    cursor: pointer;
    
    &.hidden {
      display: none;
    }
  }
}

/* ----- Image controls ----- */
.image-controls-wrapper {
  position: absolute;
  bottom: 1.5rem;
  left: 20%;
  right: calc(2.8rem + 20%); /* Updated to use fixed width */
  z-index: 5;
}

/* ----- Annotation list ----- */
.annotations-table-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 2.8rem; /* Updated to use fixed width */
  z-index: 40;
  pointer-events: none;
}

.image-title {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);

  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  background-color: rgba(255, 255, 255, 0.6);
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);

  pointer-events: none; /* 컨테이너 자체는 클릭 통과 */
  z-index: 900;
}

.image-title > * {
  pointer-events: auto; /* 실제 글자 부분은 필요시 클릭 가능하게 */
}
</style>
